/**
 * Contexto de navegación Datos Maestros — independiente del módulo Visor SAT en UI.
 * Las pantallas compartidas (/clientes, CSF, descarga SAT…) conservan la misma ruta y APIs;
 * solo cambia el retorno y se oculta el subnav operativo del Visor.
 */
import { RETURN_TO_QUERY_PARAM } from "@/lib/equipoNavigation";
import { DATOS_MAESTROS_PATH, DATOS_MAESTROS_CHECK_ID_PATH, DATOS_MAESTROS_VALIDACION_RFC_SAT_PATH } from "@/modules/datosMaestros/datosMaestrosHub";

export { DATOS_MAESTROS_PATH };

export const DATOS_MAESTROS_NAV_CONTEXT = "datos_maestros";
export const DATOS_MAESTROS_RETURN_SESSION_KEY = "vf:datos_maestros_return_to";
export const DATOS_MAESTROS_PANEL_TAB = "/?panel=datos-maestros";

/** Rutas abiertas desde tarjetas del hub Datos Maestros. */
export const DATOS_MAESTROS_HUB_CARD_PATHS = new Set([
  "/clientes",
  "/documentos-fiscales-sat",
  "/descarga-automatica-sat",
  "/monitor-sincronizacion-sat",
  "/auditoria-efos",
  "/auditoria-estatus-local",
  DATOS_MAESTROS_CHECK_ID_PATH,
  DATOS_MAESTROS_VALIDACION_RFC_SAT_PATH,
  "/laboral/validar-inscribir",
]);

export function isDatosMaestrosHubCardPath(pathname) {
  const path = String(pathname || "").split("?")[0];
  return DATOS_MAESTROS_HUB_CARD_PATHS.has(path);
}

/** Rutas del hub DM que activan Asistente Laboral al abrir (excluye /clientes compartido). */
export const DATOS_MAESTROS_LABOR_MODULE_CARD_PATHS = new Set([
  DATOS_MAESTROS_CHECK_ID_PATH,
  DATOS_MAESTROS_VALIDACION_RFC_SAT_PATH,
  "/laboral/validar-inscribir",
]);

/** Módulo de trabajo al abrir tarjetas DM (visor_sat | asistente_laboral). */
export const DATOS_MAESTROS_WORK_MODULE = {
  VISOR: "visor_sat",
  LABORAL: "asistente_laboral",
};

export function shouldSwitchToLaboralModuleFromDatosMaestros(pathname) {
  const path = String(pathname || "").split("?")[0];
  return DATOS_MAESTROS_LABOR_MODULE_CARD_PATHS.has(path);
}

/**
 * Módulo activo en UI al abrir una tarjeta desde Datos Maestros.
 * Catálogo compartido y pantallas SAT → Visor; Check ID → Laboral.
 * @param {string} pathname
 * @returns {"visor_sat"|"asistente_laboral"|null}
 */
export function resolveWorkModuleForDatosMaestrosCard(pathname) {
  const path = String(pathname || "").split("?")[0];
  if (!isDatosMaestrosHubCardPath(path)) return null;
  if (shouldSwitchToLaboralModuleFromDatosMaestros(path)) {
    return DATOS_MAESTROS_WORK_MODULE.LABORAL;
  }
  return DATOS_MAESTROS_WORK_MODULE.VISOR;
}

/**
 * @param {string} path
 */
export function isValidDatosMaestrosReturnPath(path) {
  const raw = String(path || "").trim();
  if (!raw) return false;
  if (raw === DATOS_MAESTROS_PATH) return true;
  if (raw === "/" || raw.startsWith("/?")) {
    if (raw.includes("panel=datos-maestros")) return true;
    if (raw === "/") return true;
    try {
      const url = new URL(raw, "http://local");
      return url.pathname === "/" && url.searchParams.get("panel") === "datos-maestros";
    } catch {
      return raw === "/";
    }
  }
  return false;
}

export function normalizeDatosMaestrosReturnTarget(path) {
  const raw = String(path || "").trim();
  if (!raw || raw === "/") return DATOS_MAESTROS_PANEL_TAB;
  if (raw === DATOS_MAESTROS_PATH) return DATOS_MAESTROS_PATH;
  if (raw.includes("panel=datos-maestros")) return DATOS_MAESTROS_PANEL_TAB;
  return isValidDatosMaestrosReturnPath(raw) ? raw : DATOS_MAESTROS_PATH;
}

/**
 * @param {string} [search]
 */
export function parseDatosMaestrosReturnFromSearch(search = "") {
  try {
    const params = new URLSearchParams(String(search || "").replace(/^\?/, ""));
    const raw = String(params.get(RETURN_TO_QUERY_PARAM) || "").trim();
    return isValidDatosMaestrosReturnPath(raw) ? normalizeDatosMaestrosReturnTarget(raw) : "";
  } catch {
    return "";
  }
}

export function rememberDatosMaestrosReturnTarget(returnTo) {
  const path = normalizeDatosMaestrosReturnTarget(returnTo);
  try {
    sessionStorage.setItem(DATOS_MAESTROS_RETURN_SESSION_KEY, path);
  } catch {
    /* ignore */
  }
}

export function readDatosMaestrosReturnFromSession() {
  try {
    const raw = sessionStorage.getItem(DATOS_MAESTROS_RETURN_SESSION_KEY);
    return isValidDatosMaestrosReturnPath(raw) ? normalizeDatosMaestrosReturnTarget(raw) : "";
  } catch {
    return "";
  }
}

/**
 * @param {{ state?: object, search?: string } | null} location
 */
export function hasDatosMaestrosNavigationContext(location) {
  const state = location?.state || {};
  if (state.navigationContext === DATOS_MAESTROS_NAV_CONTEXT) return true;
  const fromState = String(state.returnTo || "").trim();
  if (fromState && isValidDatosMaestrosReturnPath(fromState)) return true;
  if (parseDatosMaestrosReturnFromSearch(location?.search)) return true;
  if (readDatosMaestrosReturnFromSession()) return true;
  return false;
}

/**
 * @param {{ pathname?: string, state?: object, search?: string } | null} location
 */
export function isDatosMaestrosNavigationActive(location) {
  const path = String(location?.pathname || "").split("?")[0];
  if (!isDatosMaestrosHubCardPath(path)) return false;
  return hasDatosMaestrosNavigationContext(location);
}

/** Rutas abiertas desde Datos Maestros: no redirigir al inicio del módulo activo en sidebar. */
export function shouldBypassWorkModulePathGuard(location) {
  const path = String(location?.pathname || "").split("?")[0];
  if (!isDatosMaestrosHubCardPath(path)) return false;
  const state = location?.state || {};
  if (state.navigationContext === DATOS_MAESTROS_NAV_CONTEXT) return true;
  if (parseDatosMaestrosReturnFromSearch(location?.search)) return true;
  if (readDatosMaestrosReturnFromSession()) return true;
  return isDatosMaestrosNavigationActive(location);
}

/** Permite deep links de tarjetas DM aunque el módulo activo en sidebar sea otro. */
export function isDatosMaestrosAutonomousPath(pathname, search = "", state = null) {
  const path = String(pathname || "").split("?")[0];
  if (!isDatosMaestrosHubCardPath(path)) return false;
  const navState = state || {};
  if (navState.navigationContext === DATOS_MAESTROS_NAV_CONTEXT) return true;
  if (parseDatosMaestrosReturnFromSearch(search)) return true;
  return Boolean(readDatosMaestrosReturnFromSession());
}

/**
 * @param {{ state?: object, search?: string } | null} location
 * @param {string} [fallback]
 */
export function resolveDatosMaestrosReturnTarget(location, fallback = DATOS_MAESTROS_PATH) {
  const state = location?.state || {};
  if (state.navigationContext === DATOS_MAESTROS_NAV_CONTEXT) {
    return normalizeDatosMaestrosReturnTarget(state.returnTo || fallback);
  }
  const fromState = String(state.returnTo || "").trim();
  if (isValidDatosMaestrosReturnPath(fromState)) {
    return normalizeDatosMaestrosReturnTarget(fromState);
  }
  const fromQuery = parseDatosMaestrosReturnFromSearch(location?.search);
  if (fromQuery) return fromQuery;
  const fromSession = readDatosMaestrosReturnFromSession();
  if (fromSession) return fromSession;
  return normalizeDatosMaestrosReturnTarget(fallback);
}

/**
 * @param {string} returnTo
 * @param {{ returnLabel?: string }} [options]
 */
export function buildDatosMaestrosCardNavigationState(returnTo, options = {}) {
  const target = normalizeDatosMaestrosReturnTarget(returnTo);
  rememberDatosMaestrosReturnTarget(target);
  const state = {
    navigationContext: DATOS_MAESTROS_NAV_CONTEXT,
    returnTo: target,
    returnLabel: options.returnLabel || "Volver a Datos Maestros",
  };
  return state;
}

/**
 * @param {string} cardPath
 * @param {string} returnTo
 */
export function buildDatosMaestrosCardPathWithReturn(cardPath, returnTo) {
  const path = String(cardPath || "").split("?")[0];
  const back = normalizeDatosMaestrosReturnTarget(returnTo);
  return `${path}?${RETURN_TO_QUERY_PARAM}=${encodeURIComponent(back)}`;
}

/**
 * @param {{ state?: object, search?: string } | null} location
 */
export function captureDatosMaestrosReturnContext(location) {
  if (!hasDatosMaestrosNavigationContext(location)) return null;
  const target = resolveDatosMaestrosReturnTarget(location);
  rememberDatosMaestrosReturnTarget(target);
  return target;
}

export function clearDatosMaestrosReturnContext() {
  try {
    sessionStorage.removeItem(DATOS_MAESTROS_RETURN_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Breadcrumb de pantallas compartidas según contexto de entrada.
 * @param {{ pathname?: string, state?: object, search?: string } | null} location
 * @param {string} moduleRootLabel
 * @param {string} pageLabel
 */
export function resolveDatosMaestrosAwareBreadcrumb(location, moduleRootLabel, pageLabel) {
  if (isDatosMaestrosNavigationActive(location)) {
    return ["Datos Maestros", pageLabel];
  }
  return [moduleRootLabel, pageLabel];
}
